import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import SignatureUploader from "../SignatureUploader";
import { Buildings, PenNib, Image as ImageIcon, PencilSimple, CheckCircle, Warning } from "@phosphor-icons/react";
import { isCompanyRegistered } from "../../lib/company";

export default function CompanyTab({ requiredSetup = false, onSetupComplete, onProfileUpdate }) {
  const t = useT();
  const [profile, setProfile] = useState(null);
  const [logo, setLogo] = useState(null);
  const [sig, setSig] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [operatingRoleView, setOperatingRoleView] = useState("");
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleError, setRoleError] = useState("");
  const [roleSaved, setRoleSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [p, l, s] = await Promise.all([
        api.get("/user/profile"),
        api.get("/user/logo"),
        api.get("/user/signature"),
      ]);
      setProfile(p.data);
      setOperatingRoleView(p.data.operating_role_view || "");
      onProfileUpdate?.(p.data);
      setLogo(l.data.has_logo ? l.data : null);
      setSig(s.data.has_signature ? s.data : null);
    } catch (error) {
      setLoadError(error.response?.data?.detail || t("company.profile.error"));
    } finally {
      setLoading(false);
    }
  }, [onProfileUpdate, t]);

  useEffect(() => { load(); }, [load]);

  async function saveOperatingRoleView() {
    setRoleError("");
    setRoleSaved(false);
    setRoleSaving(true);
    try {
      await api.put("/user/operating-role-view", { role: operatingRoleView });
      const { data } = await api.get("/user/profile");
      setProfile(data);
      setOperatingRoleView(data.operating_role_view || "");
      onProfileUpdate?.(data);
      setRoleSaved(true);
    } catch (error) {
      setRoleError(error.response?.data?.detail || t("company.role_view.save_error"));
    } finally {
      setRoleSaving(false);
    }
  }

  useEffect(() => {
    if (requiredSetup && profile && !isCompanyRegistered(profile)) setShowModal(true);
  }, [requiredSetup, profile]);

  if (loading && !profile) return <div className="font-mono text-sm text-zinc-500">{t("dash.loading")}</div>;
  if (!profile) {
    return (
      <div className="grid-card p-5 space-y-3" role="alert">
        <p className="text-sm text-red-700">{loadError || t("company.profile.error")}</p>
        <button onClick={load} className="btn-primary px-3 py-2 text-sm">{t("app.retry")}</button>
      </div>
    );
  }

  const rows = [
    [t("docmgmt.company_name"), profile.company_name],
    [t("docmgmt.operator_type"), profile.economic_operator_type],
    [t("docmgmt.company_address"), profile.company_address],
    [t("docmgmt.company_country"), profile.company_country],
    [t("docmgmt.eudamed_srn"), profile.eudamed_srn],
    [t("prrc.title"), profile.responsible_person],
  ];

  return (
    <div className="space-y-5" data-testid="company-tab">
      {loadError && <p className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert">{loadError}</p>}
      <div className="grid-card p-5 flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2">
            <Buildings size={20} weight="duotone" className="text-blue-700" />
            <h2 className="text-lg font-extrabold tracking-tight">{t("company.settings_title")}</h2>
          </div>
          <p className="text-sm text-zinc-600 mt-1">{t("company.settings_sub")}</p>
          {requiredSetup && !isCompanyRegistered(profile) && (
            <p className="mt-2 text-sm font-semibold text-amber-800">{t("company.setup.sub")}</p>
          )}
        </div>
        <button onClick={() => setShowModal(true)} data-testid="company-edit-btn"
          className="btn-primary inline-flex items-center gap-2 px-3 py-2 text-xs shrink-0">
          <PencilSimple size={12} weight="bold" />
          {requiredSetup && !isCompanyRegistered(profile) ? t("company.register") : t("company.edit")}
        </button>
      </div>

      <section className="grid-card p-4 sm:p-5" data-testid="workspace-role-settings">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <h3 className="text-sm font-bold text-[var(--text)]">{t("company.role_view.title")}</h3>
            <p className="mt-1 text-xs leading-relaxed text-[var(--text-2)]">{t("company.role_view.note")}</p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:min-w-72 sm:flex-row">
            <label className="sr-only" htmlFor="workspace-role-view">{t("company.role_view.title")}</label>
            <select
              id="workspace-role-view"
              data-testid="workspace-role-view"
              value={operatingRoleView}
              onChange={(event) => { setOperatingRoleView(event.target.value); setRoleSaved(false); }}
              disabled={profile.can_manage_operating_role_view === false || roleSaving}
              className="min-w-0 flex-1 rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text)]"
            >
              <option value="">{t("company.role_view.choose")}</option>
              <option value="manufacturer">{t("role.manufacturer")}</option>
              <option value="importer">{t("role.importer")}</option>
              <option value="distributor">{t("role.distributor")}</option>
            </select>
            <button
              type="button"
              data-testid="workspace-role-save"
              onClick={saveOperatingRoleView}
              disabled={!operatingRoleView || profile.can_manage_operating_role_view === false || roleSaving}
              className="btn-primary shrink-0 px-3 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
            >
              {roleSaving ? t("company.role_view.saving") : t("company.role_view.save")}
            </button>
          </div>
        </div>
        {profile.can_manage_operating_role_view === false && (
          <p className="mt-2 text-xs text-[var(--text-2)]">{t("company.role_view.owner_only")}</p>
        )}
        {roleError && <p role="alert" className="mt-2 text-xs text-red-500">{roleError}</p>}
        {roleSaved && <p role="status" className="mt-2 text-xs text-emerald-600">{t("company.role_view.saved")}</p>}
      </section>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="grid-card p-4 md:col-span-2">
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">{t("docmgmt.company_section")}</div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {rows.map(([label, value]) => (
              <div key={label}>
                <dt className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">{label}</dt>
                <dd className="text-sm font-semibold text-zinc-800 mt-0.5">{value || "—"}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="grid-card p-4 space-y-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2 inline-flex items-center gap-1">
              <ImageIcon size={12} /> {t("docmgmt.logo_section")}
            </div>
            {logo ? (
              <div className="border border-dashed border-zinc-300 rounded-sm p-2 bg-white flex items-center justify-center min-h-[56px]">
                <img src={logo.data_url} alt="logo" className="max-h-12 object-contain" data-testid="company-logo-preview" />
              </div>
            ) : (
              <StatusLine ok={false} msg={t("docmgmt.no_logo")} />
            )}
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2 inline-flex items-center gap-1">
              <PenNib size={12} /> {t("docmgmt.signature_section")}
            </div>
            {sig ? (
              <div className="border border-dashed border-zinc-300 rounded-sm p-2 bg-white flex items-center justify-center min-h-[56px]">
                <img src={sig.data_url} alt="signature" className="max-h-12 object-contain" data-testid="company-sig-preview" />
              </div>
            ) : (
              <StatusLine ok={false} msg={t("sig.none")} />
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <SignatureUploader
          requiredCompany={requiredSetup && !isCompanyRegistered(profile)}
          onClose={() => setShowModal(false)}
          onChange={load}
          onCompanySaved={async () => {
            await load();
            setShowModal(false);
            onSetupComplete?.();
          }}
        />
      )}
    </div>
  );
}

function StatusLine({ ok, msg }) {
  const Icon = ok ? CheckCircle : Warning;
  return (
    <div className={`inline-flex items-center gap-1.5 text-xs ${ok ? "text-emerald-700" : "text-amber-700"}`}>
      <Icon size={12} weight="fill" /> {msg}
    </div>
  );
}
